import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { Pager, SearchInput } from "@/components/common/Pager";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarClock, Clock, Truck, MapPin, AlertTriangle, Route as RouteIcon,
  ChevronRight, PackageCheck, Store, CalendarDays, Zap, X,
} from "lucide-react";

const INDIGO = "#4F46E5";

const CUSTOMER_TINT = {
  "M&S": "#0f766e", "Frasers": "#4F46E5", "TRAC": "#B45309",
  "M&S Head Office": "#0891b2", Unassigned: "#6a6d70",
};
const tint = (c) => CUSTOMER_TINT[c] || "#8B5CF6";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const dayShort = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long" }) : "");

export default function DeliveryScheduling() {
  const { activeSite } = useSite();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState("ALL");
  const [region, setRegion] = useState("ALL");
  const [selDate, setSelDate] = useState(null);
  const [fmOnly, setFmOnly] = useState(false);

  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/scheduling/overview", { params: { site: activeSite, customer, region } });
      setOv(data);
    } catch (e) { toast.error("Could not load schedule overview"); }
    finally { setLoading(false); }
  };

  const loadList = async () => {
    const params = { site: activeSite, customer, region, page, page_size: 25 };
    if (selDate) params.date = selDate;
    if (fmOnly) params.final_mile = true;
    if (q) params.q = q;
    try { const { data } = await api.get("/scheduling/schedules", { params }); setList(data); }
    catch (e) { /* silent */ }
  };

  useEffect(() => { setLoading(true); loadOverview(); /* eslint-disable-next-line */ }, [activeSite, customer, region]);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [activeSite, customer, region, selDate, fmOnly, q, page]);
  useEffect(() => { setPage(1); }, [selDate, fmOnly, q, customer, region]);

  const maxCount = useMemo(() => Math.max(1, ...((ov?.board || []).map((b) => b.count))), [ov]);

  if (loading || !ov) return <Loader label="Loading delivery schedules…" />;

  const k = ov.kpis;
  const empty = k.total === 0;

  return (
    <div>
      <PageHeader title="Delivery Scheduling" icon={CalendarClock} accent={INDIGO}
        subtitle="Customer delivery-day calendar with cut-off tracking, final-mile flags and region load across the retail store network." />

      {empty ? (
        <div data-testid="sched-empty" className="rounded-xl border border-[#e5e9f0] bg-white p-10 text-center">
          <CalendarDays size={30} className="mx-auto mb-3 text-[#c8cfda]" />
          <div className="text-sm text-[#6a6d70]">No delivery schedules loaded yet — store schedule uploads will populate this view.</div>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <KpiCard testId="sched-kpi-total" label="Upcoming Deliveries" value={k.total.toLocaleString()} accent={INDIGO} />
            <KpiCard testId="sched-kpi-dates" label="Delivery Days" value={k.dates} accent="#0284C7" />
            <KpiCard testId="sched-kpi-cutoff" label="Cut-offs Today" value={k.cutoff_today} accent={k.cutoff_today ? "#EF4444" : "#10B981"} />
            <KpiCard testId="sched-kpi-today" label="Delivering Today" value={k.deliv_today} accent="#F59E0B" />
            <KpiCard testId="sched-kpi-fm" label="Final-Mile Drops" value={k.final_mile.toLocaleString()} accent="#8B5CF6" />
            <KpiCard testId="sched-kpi-changed" label="Schedule Changes" value={k.changed} accent={k.changed ? "#EF4444" : "#10B981"} />
          </div>

          {/* Filters */}
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <select data-testid="sched-customer" value={customer} onChange={(e) => setCustomer(e.target.value)}
              className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
              <option value="ALL">All customers</option>
              {ov.customers.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="sched-region" value={region} onChange={(e) => setRegion(e.target.value)}
              className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
              <option value="ALL">All regions</option>
              {ov.regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button data-testid="sched-fm-toggle" onClick={() => setFmOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition ${fmOnly ? "border-[#8B5CF6] bg-[#8B5CF6]/10 text-[#8B5CF6]" : "border-[#e5e9f0] text-[#6a6d70] hover:border-[#8B5CF6]/40"}`}>
              <Zap size={14} /> Final-mile only</button>
            {selDate && (
              <button data-testid="sched-clear-day" onClick={() => setSelDate(null)}
                className="inline-flex items-center gap-1 rounded-lg border border-[#4F46E5]/40 bg-[#4F46E5]/10 px-3 py-2 text-sm font-semibold text-[#4F46E5]">
                <X size={13} /> {fmtDate(selDate)}</button>)}
            <div className="ml-auto w-full sm:w-72"><SearchInput testId="sched-search" value={q} onChange={setQ} placeholder="Search store, ship-to, region…" /></div>
          </div>

          {/* Delivery-day board */}
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><CalendarDays size={15} /> Delivery Day Board</h3>
          <div data-testid="sched-board" className="mb-8 flex gap-3 overflow-x-auto pb-3">
            {ov.board.map((b) => {
              const active = selDate === b.date;
              const urgent = b.cutoff_today;
              const passed = b.cutoff_passed;
              return (
                <div key={b.date} data-testid={`sched-day-${b.date}`}
                  className={`w-60 shrink-0 rounded-xl border bg-white shadow-sm transition ${active ? "border-[#4F46E5] ring-2 ring-[#4F46E5]/30" : "border-[#e5e9f0] hover:border-[#4F46E5]/40"}`}>
                  <button onClick={() => setSelDate(active ? null : b.date)} className="w-full p-4 text-left">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]">{dayShort(b.date)}</div>
                        <div className="text-lg font-semibold text-[#32363a]">{fmtDate(b.date)}</div>
                      </div>
                      <span className="rounded-lg px-2 py-1 text-sm font-bold text-white" style={{ background: INDIGO }}>{b.count}</span>
                    </div>
                    {/* load bar */}
                    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#eef1f5]">
                      <div className="h-1.5 rounded-full" style={{ width: `${Math.round((b.count / maxCount) * 100)}%`, background: INDIGO }} />
                    </div>
                    {/* cut-off */}
                    <div className={`mt-3 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${urgent ? "bg-red-100 text-red-700" : passed ? "bg-[#eef1f5] text-[#6a6d70]" : "bg-amber-100 text-amber-700"}`}>
                      <Clock size={11} /> Cut-off {fmtDate(b.cutoff_date)}{urgent ? " · TODAY" : passed ? " · closed" : ""}
                    </div>
                    {/* customer chips */}
                    <div className="mt-3 flex flex-wrap gap-1">
                      {b.customers.slice(0, 3).map((c) => (
                        <span key={c.customer} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ background: tint(c.customer) }}>
                          {c.customer} · {c.count}</span>))}
                    </div>
                    {/* footer stats */}
                    <div className="mt-3 flex items-center gap-3 text-[11px] text-[#6a6d70]">
                      <span className="inline-flex items-center gap-1"><MapPin size={11} /> {b.regions.length} regions</span>
                      {b.final_mile > 0 && <span className="inline-flex items-center gap-1 text-[#8B5CF6]"><Zap size={11} /> {b.final_mile} FM</span>}
                      {b.changed > 0 && <span className="inline-flex items-center gap-1 text-red-600"><AlertTriangle size={11} /> {b.changed}</span>}
                    </div>
                  </button>
                  <button data-testid={`sched-plan-${b.date}`} onClick={() => navigate("/transport")}
                    className="flex w-full items-center justify-center gap-1 border-t border-[#f0f2f5] px-4 py-2 text-[11px] font-semibold text-[#0284C7] hover:bg-[#0284C7]/5">
                    <RouteIcon size={12} /> Plan routes in POD <ChevronRight size={12} /></button>
                </div>
              );
            })}
          </div>

          {/* Schedule list */}
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">
            <Store size={15} /> {selDate ? `Schedules for ${fmtDate(selDate)}` : "All Upcoming Schedules"}
            {list && <span className="font-mono text-xs font-normal text-[#89919a]">· {list.total.toLocaleString()}</span>}
          </h3>
          <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#f7f8fa] text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
                <tr>
                  <th className="px-4 py-2.5">Store</th><th>Ship-To</th><th>Customer</th><th>Region</th>
                  <th>Cut-off</th><th>Delivery</th><th className="text-center">Flags</th>
                </tr>
              </thead>
              <tbody data-testid="sched-table">
                {(list?.items || []).map((r) => (
                  <tr key={r.id} data-testid={`sched-row-${r.qb_record_id}`} className="border-t border-[#f0f2f5] hover:bg-[#f7f8fa]">
                    <td className="px-4 py-2.5 font-mono text-xs text-[#4F46E5]">{r.store_code || "—"}</td>
                    <td className="text-[#32363a]">{r.ship_to_name || "—"}</td>
                    <td><span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ background: tint(r.customer) }}>{r.customer}</span></td>
                    <td className="text-[#6a6d70]">{r.region}</td>
                    <td className="text-[13px] text-[#6a6d70]"><span className="font-medium text-[#32363a]">{r.cut_off_day}</span> {fmtDate(r.cut_off_date)}</td>
                    <td className="text-[13px] text-[#6a6d70]"><span className="font-medium text-[#32363a]">{r.delivery_day}</span> {fmtDate(r.delivery_date)}</td>
                    <td className="text-center">
                      <div className="inline-flex gap-1">
                        {r.final_mile && <span title={r.final_mile_text || "Final mile"} className="inline-flex items-center gap-0.5 rounded bg-[#8B5CF6]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#8B5CF6]"><Zap size={10} /> FM</span>}
                        {r.schedule_changed && <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700"><AlertTriangle size={10} /></span>}
                        {r.during_bank_holiday && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">BH</span>}
                        {!r.final_mile && !r.schedule_changed && !r.during_bank_holiday && <PackageCheck size={13} className="text-[#c8cfda]" />}
                      </div>
                    </td>
                  </tr>
                ))}
                {list && list.items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#6a6d70]">No schedules match these filters.</td></tr>)}
              </tbody>
            </table>
          </div>
          {list && <Pager testId="sched-pager" page={list.page} pages={list.pages} total={list.total} pageSize={list.page_size} onPage={setPage} />}
        </>
      )}
    </div>
  );
}
